import { z } from 'zod';

// T-215 (collettiva): documenti di trasporto in uscita.
// - kind='ddt': DDT ufficiale verso una destinazione (DPR 472/1996; lotto per
//   riga obbligatorio per alimenti non preimballati ex Dir. 2011/91/UE;
//   temperature/orari richiesti dai capitolati pubblici dei pasti veicolati).
// - kind='trasferimento': documento interno magazzino → impianto (sedi
//   separate dello stesso tenant). Nessun movimento stock in v1: lo stock
//   scende quando l'impianto consuma in produzione (pool tenant-wide).
// Le bozze sono offline-first (master sync); l'EMISSIONE è online-only via
// RPC issue_shipment() che assegna il numero progressivo per tenant/kind/anno.
export const SHIPMENT_KINDS = ['ddt', 'trasferimento'] as const;
export const ShipmentKindSchema = z.enum(SHIPMENT_KINDS);
export type ShipmentKind = z.infer<typeof ShipmentKindSchema>;

export const SHIPMENT_STATUSES = ['draft', 'issued', 'void'] as const;
export const ShipmentStatusSchema = z.enum(SHIPMENT_STATUSES);
export type ShipmentStatus = z.infer<typeof ShipmentStatusSchema>;

export const ShipmentLineSchema = z.object({
  production_id: z.string().uuid().nullable().optional(),
  food_id: z.string().uuid().nullable().optional(),
  // T-481: explicit physical semantics. Optional only while reading drafts
  // created before the contract; every new mobile writer persists the field.
  stock_effect_v1: z.enum(['dispatch', 'none']).optional(),
  description: z.string().min(1).max(300),
  batch_lot: z.string().min(1).max(100),
  qty: z.number().positive(),
  uom: z.string().min(1).max(20),
  containers: z.string().max(100).nullable().optional(),
  notes: z.string().max(300).nullable().optional(),
});
export type ShipmentLine = z.infer<typeof ShipmentLineSchema>;

export const ShipmentSupplyAllocationLineV1Schema = z.object({
  request_line_index: z.number().int().nonnegative(),
  description: z.string().min(1).max(300),
  qty: z.number().positive(),
  uom: z.string().min(1).max(20).nullable(),
});
export type ShipmentSupplyAllocationLineV1 = z.infer<
  typeof ShipmentSupplyAllocationLineV1Schema
>;

export const ShipmentSupplyAllocationV1Schema = z.object({
  request_id: z.string().uuid(),
  meal_service: z.string().trim().min(1).max(20),
  lines: z.array(ShipmentSupplyAllocationLineV1Schema).min(1).max(1_000),
});
export type ShipmentSupplyAllocationV1 = z.infer<
  typeof ShipmentSupplyAllocationV1Schema
>;

export const ShipmentSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  business_id: z.string().uuid(),
  kind: ShipmentKindSchema.default('ddt'),
  document_scope: z.enum(['collective', 'restaurant']).default('collective'),
  status: ShipmentStatusSchema.default('draft'),
  doc_number: z.number().int().positive().nullable(),
  doc_year: z.number().int().nullable(),
  destination_id: z.string().uuid().nullable(),
  to_business_id: z.string().uuid().nullable(),
  // T-447: servizio del DDT collettiva (pranzo/cena/colazione) — uno per documento,
  // mostrato in intestazione. Null = legacy/non-collettiva (deriva dai suffissi/produzioni).
  meal_service: z.string().max(20).nullable().optional(),
  // Giorno operativo esplicito: non va ricostruito da created_at/issued_at,
  // perché una bozza preparata la sera può servire il giorno successivo.
  service_date: z.string().date().nullable().optional(),
  // Binding immutabile request+riga+servizio congelato quando la bozza viene
  // compilata dagli ordini. Il DB lo valida e deriva gli stati delle richieste
  // dalle sole allocation dei DDT issued.
  supply_allocations_v1: z
    .array(ShipmentSupplyAllocationV1Schema)
    .max(1_000)
    .default([]),
  transport_cause: z.string().max(200).default('Vendita/somministrazione pasti'),
  // Collegamento informativo facoltativo: non genera ne modifica fatture.
  invoice_reference: z.string().max(100).nullable().optional(),
  carrier_name: z.string().max(200).nullable(),
  vehicle_plate: z.string().max(20).nullable(),
  departure_at: z.string().datetime().nullable(),
  arrival_at: z.string().datetime().nullable(),
  distribution_start_at: z.string().datetime().nullable(),
  temp_hot_c: z.number().nullable(),
  temp_cold_c: z.number().nullable(),
  vehicle_check_ok: z.boolean().nullable(),
  vehicle_check_notes: z.string().nullable(),
  lines: z.array(ShipmentLineSchema).default([]),
  notes: z.string().nullable(),
  issued_at: z.string().datetime().nullable(),
  issued_by: z.string().uuid().nullable(),
  pdf_path: z.string().nullable(),
  version: z.number().int().nonnegative().default(0),
  created_at: z.string().datetime(),
  archived_at: z.string().datetime().nullable(),
});
export type Shipment = z.infer<typeof ShipmentSchema>;

// "DDT 12/2026" / "TRA 3/2026" — formato umano del numero documento.
export function formatShipmentNumber(
  docNumber: number | null | undefined,
  docYear: number | null | undefined,
  kind: ShipmentKind = 'ddt',
): string {
  if (!docNumber || !docYear) return kind === 'ddt' ? 'DDT (bozza)' : 'TRA (bozza)';
  const prefix = kind === 'ddt' ? 'DDT' : 'TRA';
  return `${prefix} ${docNumber}/${docYear}`;
}

// Lotto di default per un pasto: la convenzione dei centri cottura è il lotto
// di produzione giornaliero "L." + data (Dir. 2011/91/UE). Override-abile
// riga per riga in UI.
export function defaultMealLot(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `L.${y}-${m}-${day}`;
}
